"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Heart, BookOpen, CheckCircle, ArrowRight, ArrowLeft } from "lucide-react"
import { ministries } from "../data/mockData"
import { checkLoginStatus, getUserData } from "../api/authService"
import { postComment } from "../api/adminService"
import { Toast } from "./Toast"

interface TestimonialModalProps {
  isOpen: boolean
  onClose: () => void
}

const TestimonialModal_v1: React.FC<TestimonialModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedMinistry, setSelectedMinistry] = useState("")
  const [testimonialText, setTestimonialText] = useState("")
  const [TestimonialHeading, setTestimonialHeading] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const steps = [
    { id: 1, title: "Understanding Testimonies", icon: Heart },
    { id: 2, title: "Choose Ministry", icon: BookOpen },
    { id: 3, title: "Write Your Testimony", icon: CheckCircle },
  ]

  useEffect(() => {
    const fetchUserData = async () => {
      const loggedIn = await checkLoginStatus();
      if (!loggedIn) {
        return;
      }
      try {
        const userData = await getUserData();
        if (!userData) {
          return;
        }
        setUser(userData);
      } catch (error) {
        console.log('Error fetching user:', error);
      }
    };

    fetchUserData();
  }, [])


  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const savePendingActivity = (testimonialPayload: any) => {
    localStorage.setItem("pendingTestimonial", JSON.stringify(testimonialPayload));
  };

  const handleSubmit = async () => {
    const ministry = ministries.find(m => m.id === selectedMinistry);

    const testimonialPayload = {
      title: TestimonialHeading,
      ministry,
      content: testimonialText,
    };
    setLoading(true);

    if (!user || !user.id) {
      savePendingActivity(testimonialPayload);
      setShowLoginPrompt(true); // instead of redirect
      setLoading(false);
      return;
    }

    try {
      const { success, message } = await postComment(testimonialPayload, user);
      if (!success) {
        Toast.warning(message || "Failed To Share Testimony");
      } else {
        Toast.success("Testimony shared successfully!");
        onClose();
        resetForm();
      }
    } catch (error: any) {
      console.log(error);
      Toast.error(error.message || "Error submitting testimony");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1)
    setSelectedMinistry("")
    setTestimonialText("")
    setTestimonialHeading("")
  }

  const handleSubmit_ = () => {
    // Handle testimonial submission
    console.log("Submitting testimonial:", {
      user,
      title: TestimonialHeading,
      ministry: ministries[JSON.parse(selectedMinistry)],
      content: testimonialText,
    })
    const { } = postComment()
    onClose()
    // Reset form
    setCurrentStep(1)
    setSelectedMinistry("")
    setTestimonialText("")
    setTestimonialHeading("")
  }

  const testimonialTemplate = `I want to share how [Ministry Name] has impacted my life...

Before joining this ministry, I was [describe your situation/struggle]...

Through [specific experience/event], God showed me [what you learned/how you grew]...

Now I can say that [how your life has changed/what you've gained]...

I encourage others to [call to action/invitation]...`

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-700/50"
        >
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 text-white p-6 relative border-b border-gray-600/50">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold mb-2 text-gray-100">Share Your Testimony</h2>
            <p className="text-gray-300">Let your story inspire others in their faith journey</p>
          </div>

          <div className="px-6 py-4 bg-gray-900/50 border-b border-gray-700/50">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${currentStep >= step.id
                      ? "bg-gradient-to-r from-amber-600 to-purple-600 text-white shadow-lg"
                      : "bg-gray-800 text-gray-400 border border-gray-600/50"
                      }`}
                  >
                    <step.icon className="h-5 w-5" />
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-16 h-0.5 mx-2 transition-colors ${currentStep > step.id ? "bg-gradient-to-r from-amber-600 to-purple-600" : "bg-gray-700"
                        }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {currentStep === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div className="text-center">
                  <Heart className="h-16 w-16 text-rose-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">The Power of Testimony</h3>
                  <p className="text-gray-300">Your story matters and can transform lives</p>
                </div>

                <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700/50">
                  <h4 className="font-semibold text-gray-100 mb-3">What makes a powerful testimony?</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Share your authentic experience with God</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Describe how God worked in your specific situation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Highlight the transformation or growth you experienced</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Encourage others in their faith journey</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-amber-900/30 to-purple-900/30 border border-amber-600/30 rounded-xl p-4">
                  <p className="text-gray-200 text-sm italic">
                    "Let the redeemed of the LORD tell their story—those he redeemed from the land of the foe" - Psalm
                    107:2
                  </p>
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div className="text-center">
                  <BookOpen className="h-16 w-16 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">Choose Your Ministry</h3>
                  <p className="text-gray-300">Select the ministry area your testimony relates to</p>
                </div>

                <div className="grid gap-4">
                  {ministries.map((ministry) => (
                    <motion.div
                      key={ministry.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedMinistry(ministry.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedMinistry === ministry.id
                        ? "border-purple-500 bg-gradient-to-r from-purple-900/40 to-amber-900/40 shadow-lg shadow-purple-900/30"
                        : "border-gray-600/50 hover:border-gray-500 bg-gray-800/40 hover:bg-gray-700/40"
                        }`}
                    >
                      <h4 className="font-semibold text-gray-100 mb-1">{ministry.name}</h4>
                      <p className="text-gray-400 text-sm">{ministry.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div className="text-center">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">Write Your Testimony</h3>
                  <p className="text-gray-300">Share your heart and inspire others</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Testimony Title</label>
                    <input
                      type="text"
                      value={TestimonialHeading}
                      onChange={(e) => setTestimonialHeading(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-600/50 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-gray-800/60 text-gray-100 placeholder-gray-500"
                      placeholder="I am no longer bound by fear..."
                    />
                  </div>

                  <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700/50">
                    <h4 className="font-medium text-gray-100 mb-2">Template Guide:</h4>
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">{testimonialTemplate}</pre>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Your Testimony</label>
                    <textarea
                      value={testimonialText}
                      onChange={(e) => setTestimonialText(e.target.value)}
                      rows={8}
                      className="w-full px-4 py-3 border border-gray-600/50 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none bg-gray-800/60 text-gray-100 placeholder-gray-500"
                      placeholder="Share your testimony here..."
                    />
                  </div>
                </div>
              </motion.div>
            )}
            <div className="px-6 py-4 bg-gray-900/60 flex items-center justify-between border-t border-gray-700/50">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${currentStep === 1 ? "text-gray-600 cursor-not-allowed" : "text-gray-300 hover:bg-gray-800/60"
                  }`}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={currentStep === 2 && !selectedMinistry}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-amber-600 to-purple-600 text-white rounded-lg font-medium hover:from-amber-500 hover:to-purple-500 disabled:from-gray-700 disabled:to-gray-800 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!TestimonialHeading.trim() || !testimonialText.trim() || loading}
                  className=" px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-500 hover:to-emerald-500 disabled:from-gray-700 disabled:to-gray-800 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
                >
                  {loading ? "Submitting..." : "Submit Testimony"}
                </button>
              )}
            </div>
          </div>


        </motion.div>
      </div>
      <LoginPromptModal isOpen={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} />
    </AnimatePresence>
  )
}


const LoginPromptModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
        className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-gray-700"
      >
        <h2 className="text-xl font-bold text-white mb-1">Login Required</h2>
        <p className="text-sm text-gray-400 mb-3">Your testimony is pending</p>

        <p className="text-gray-300 mb-6">
          Please login or signup to share your testimony. We saved your testimony and it will be posted once you log in.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <a
            href="/login"
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-600 to-purple-600 text-white hover:from-amber-500 hover:to-purple-500 transition-all"
          >
            Login / Signup
          </a>
        </div>
      </motion.div>
    </div>
  );
};




const TestimonialModal: React.FC<TestimonialModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedMinistry, setSelectedMinistry] = useState("")
  const [testimonialText, setTestimonialText] = useState("")
  const [TestimonialHeading, setTestimonialHeading] = useState("")
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState<any>([])

  const steps = [
    { id: 1, title: "Understanding Testimonies", icon: Heart },
    { id: 2, title: "Choose Ministry", icon: BookOpen },
    { id: 3, title: "Write Your Testimony", icon: CheckCircle },
  ]

  useEffect(() => {
    const fetchUserData = async () => {
      const loggedIn = await checkLoginStatus()
      if (!loggedIn) {
        return
      }
      try {
        const userData = await getUserData()
        if (!userData) {
          return
        }
        setUser(userData)
      } catch (error) {
        console.log("Error fetching user:", error)
      }
    }

    fetchUserData()
  }, [])

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async () => {
    const ministry = ministries.find((m) => m.id === selectedMinistry)

    const testimonialPayload = {
      title: TestimonialHeading,
      ministry,
      content: testimonialText,
    }
    setLoading(true)

    try {
      const { success, message } = await postComment(testimonialPayload, user)
      if (!success) {
        Toast.warning(message || "Failed To Share Testimony")
      } else {
        Toast.success("Testimony shared successfully!")
        onClose()
        resetForm()
      }
    } catch (error: any) {
      console.log(error)
      Toast.error(error.message || "Error submitting testimony")
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setCurrentStep(1)
    setSelectedMinistry("")
    setTestimonialText("")
    setTestimonialHeading("")
  }

  const testimonialTemplate = `I want to share how [Ministry Name] has impacted my life...

Before joining this ministry, I was [describe your situation/struggle]...

Through [specific experience/event], God showed me [what you learned/how you grew]...

Now I can say that [how your life has changed/what you've gained]...

I encourage others to [call to action/invitation]...`

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-700/50"
        >
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 text-white p-6 relative border-b border-gray-600/50">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold mb-2 text-gray-100">Share Your Testimony</h2>
            <p className="text-gray-300">Let your story inspire others in their faith journey</p>
          </div>

          <div className="px-6 py-4 bg-gray-900/50 border-b border-gray-700/50">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                      currentStep >= step.id
                        ? "bg-gradient-to-r from-amber-600 to-purple-600 text-white shadow-lg"
                        : "bg-gray-800 text-gray-400 border border-gray-600/50"
                    }`}
                  >
                    <step.icon className="h-5 w-5" />
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-16 h-0.5 mx-2 transition-colors ${
                        currentStep > step.id ? "bg-gradient-to-r from-amber-600 to-purple-600" : "bg-gray-700"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {currentStep === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div className="text-center">
                  <Heart className="h-16 w-16 text-rose-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">The Power of Testimony</h3>
                  <p className="text-gray-300">Your story matters and can transform lives</p>
                </div>

                <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700/50">
                  <h4 className="font-semibold text-gray-100 mb-3">What makes a powerful testimony?</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Share your authentic experience with God</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Describe how God worked in your specific situation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Highlight the transformation or growth you experienced</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Encourage others in their faith journey</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-amber-900/30 to-purple-900/30 border border-amber-600/30 rounded-xl p-4">
                  <p className="text-gray-200 text-sm italic">
                    "Let the redeemed of the LORD tell their story—those he redeemed from the land of the foe" - Psalm
                    107:2
                  </p>
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div className="text-center">
                  <BookOpen className="h-16 w-16 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">Choose Your Ministry</h3>
                  <p className="text-gray-300">Select the ministry area your testimony relates to</p>
                </div>

                <div className="grid gap-4">
                  {ministries.map((ministry) => (
                    <motion.div
                      key={ministry.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedMinistry(ministry.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedMinistry === ministry.id
                          ? "border-purple-500 bg-gradient-to-r from-purple-900/40 to-amber-900/40 shadow-lg shadow-purple-900/30"
                          : "border-gray-600/50 hover:border-gray-500 bg-gray-800/40 hover:bg-gray-700/40"
                      }`}
                    >
                      <h4 className="font-semibold text-gray-100 mb-1">{ministry.name}</h4>
                      <p className="text-gray-400 text-sm">{ministry.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <div className="text-center">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">Write Your Testimony</h3>
                  <p className="text-gray-300">Share your heart and inspire others</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Testimony Title</label>
                    <input
                      type="text"
                      value={TestimonialHeading}
                      onChange={(e) => setTestimonialHeading(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-600/50 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-gray-800/60 text-gray-100 placeholder-gray-500"
                      placeholder="I am no longer bound by fear..."
                    />
                  </div>

                  <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700/50">
                    <h4 className="font-medium text-gray-100 mb-2">Template Guide:</h4>
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">{testimonialTemplate}</pre>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Your Testimony</label>
                    <textarea
                      value={testimonialText}
                      onChange={(e) => setTestimonialText(e.target.value)}
                      rows={8}
                      className="w-full px-4 py-3 border border-gray-600/50 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none bg-gray-800/60 text-gray-100 placeholder-gray-500"
                      placeholder="Share your testimony here..."
                    />
                  </div>
                </div>
              </motion.div>
            )}
            <div className="px-6 py-4 bg-gray-900/60 flex items-center justify-between border-t border-gray-700/50">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentStep === 1 ? "text-gray-600 cursor-not-allowed" : "text-gray-300 hover:bg-gray-800/60"
                }`}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={currentStep === 2 && !selectedMinistry}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-amber-600 to-purple-600 text-white rounded-lg font-medium hover:from-amber-500 hover:to-purple-500 disabled:from-gray-700 disabled:to-gray-800 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!TestimonialHeading.trim() || !testimonialText.trim() || loading}
                  className=" px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-500 hover:to-emerald-500 disabled:from-gray-700 disabled:to-gray-800 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
                >
                  {loading ? "Submitting..." : "Submit Testimony"}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default TestimonialModal
